/* eslint-disable no-nested-ternary */
import { format } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  DollarSign,
  FileText,
  FileX2,
  Heart,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Shield,
  Sparkles,
  Trash2,
  User,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { CitationViewer } from '@/components/agent-chat/citations/CitationViewer';
import { CitationComponent } from '@/components/agent-chat/messages/tiptap-extensions/Citations';
import { CaseContextApprovalToggle } from '@/components/case-context/CaseContextApprovalToggle';
import { CaseContextBulkApproval } from '@/components/case-context/CaseContextBulkApproval';
import { CaseContextGenerationSpinner } from '@/components/case-context/CaseContextGenerationSpinner';
import { DemandReadinessCard } from '@/components/case-context/DemandReadinessCard';
import { DocumentCompletenessCard } from '@/components/case-context/DocumentCompletenessCard';
import { DemandGenerationSpinner } from '@/components/demand/DemandGenerationSpinner';
import { SetExhibitsModal } from '@/components/demand/SetExhibitsModal';
import { TemplatePickerDialog } from '@/components/demand-template/TemplatePickerDialog';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  DemandCitationProvider,
  useDemandCitation,
} from '@/context/DemandCitationContext';
import useMatterSelection from '@/hooks/useMatterSelection';
import { cn } from '@/lib/utils';
import { useListArtifactsQuery } from '@/services/api/artifactService';
import { useDraftDemandFromTemplateMutation } from '@/services/api/demandService';
import {
  useGenerateContextMutation,
  useGetMatterContextQuery,
  useUpdateMatterContextMutation,
} from '@/services/api/matterContextService';
import type {
  DemandReadiness,
  DocumentCompletenessAssessment,
  Icd10Entry,
} from '@/services/types/case-context-types';

// Matches citation tags in any of three forms:
// 1. <cite artifact-id="A" citation-ids="page/1/block/2"/>
// 2. <cite citation-id="uuid"/>
// 3. <cite artifact-id="A"/> (artifact-id only, no citation-ids)
// Captures groups: [artifactId+citationIds, citationIds, citationIdOnly, artifactIdOnly]
const CITATION_REGEX =
  /<cite\b[^>]*?(?:artifact-id=["']([^"']+)["'][^>]*?citation-ids=["']([^"']+)["']|citation-id=["']([^"']+)["']|artifact-id=["']([^"']+)["'])[\s\S]*?>/gi;

// Utility to strip citation markup while capturing both React chips and raw tags
const parseCitations = (
  text: string,
  matterId: string,
  setSelectedCitation: ReturnType<
    typeof useDemandCitation
  >['setSelectedCitation'],
) => {
  CITATION_REGEX.lastIndex = 0;
  const chips: React.ReactNode[] = [];
  const rawTags: string[] = [];
  let match: RegExpExecArray | null;
  let idx = 1;
  // eslint-disable-next-line no-cond-assign
  while ((match = CITATION_REGEX.exec(text)) !== null) {
    rawTags.push(match[0]);

    // Handle new format: <cite citation-id="uuid"/>
    const citationIdOnly = match[3] || '';
    if (citationIdOnly) {
      chips.push(
        <CitationComponent
          key={`field-cite-${citationIdOnly}-${idx}`}
          citationId={citationIdOnly}
          matterId={matterId}
          setSelectedCitation={setSelectedCitation}
        />,
      );
      idx += 1;
    }

    // Legacy format: <cite artifact-id="A" citation-ids="page/1/block/2"/>
    // These are stripped from text but no longer rendered as badges
  }
  const cleaned = text.replace(CITATION_REGEX, '').replace(/<\/cite>/gi, '');
  return { cleaned, chips, rawTags } as const;
};

// ------------------ ICD10 Codes Editor ------------------

interface Icd10CodesEditorProps {
  codes: Record<string, string>;
  onChange: (updated: Record<string, string>) => void;
  matterId: string;
}

const Icd10CodesEditor: React.FC<Icd10CodesEditorProps> = ({
  codes,
  onChange,
  matterId,
}) => {
  const { setSelectedCitation } = useDemandCitation();
  const handleCodeChange = (oldCode: string, newCode: string) => {
    if (oldCode === newCode) return;
    const updated = { ...codes };
    const desc = updated[oldCode];
    delete updated[oldCode];
    updated[newCode] = desc;
    onChange(updated);
  };

  const handleDescChange = (code: string, newDesc: string) => {
    onChange({ ...codes, [code]: newDesc });
  };

  const handleRemove = (code: string) => {
    const updated = { ...codes };
    delete updated[code];
    onChange(updated);
  };

  const handleAddRow = () => {
    // Generate a temporary unique key. User should overwrite.
    let idx = 1;
    let newKey = `NEW_${idx}`;
    while (codes[newKey]) {
      idx += 1;
      newKey = `NEW_${idx}`;
    }
    onChange({ ...codes, [newKey]: '' });
  };

  // Helper to strip <cite> tags and build CitationComponents
  const extractCitations = (text: string) => {
    CITATION_REGEX.lastIndex = 0;
    const citations: React.ReactNode[] = [];
    let match: RegExpExecArray | null;
    let idx = 1;
    // eslint-disable-next-line no-cond-assign
    while ((match = CITATION_REGEX.exec(text)) !== null) {
      // Handle new format: <cite citation-id="uuid"/>
      const citationIdOnly = match[3] || '';
      if (citationIdOnly) {
        citations.push(
          <CitationComponent
            key={`icd-cite-${citationIdOnly}-${idx}`}
            citationId={citationIdOnly}
            matterId={matterId}
            setSelectedCitation={setSelectedCitation}
          />,
        );
        idx += 1;
      }

      // Legacy format: <cite artifact-id="A" citation-ids="page/1/block/2"/>
      // These are stripped from text but no longer rendered as badges
    }
    const cleaned = text.replace(CITATION_REGEX, '').replace(/<\/cite>/gi, '');
    return { cleaned, citations };
  };

  const entries = Object.entries(codes);

  return (
    <div>
      <Table className="border bg-muted/50 mb-2">
        <TableHeader>
          <TableRow>
            <TableHead className="w-32">Code</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map(([code, desc]) => (
            <TableRow key={code} className="hover:bg-transparent">
              <TableCell className="p-1">
                <Input
                  value={code}
                  onChange={(e) => handleCodeChange(code, e.target.value)}
                  className="h-8 font-mono text-xs"
                />
              </TableCell>
              <TableCell className="p-1">
                {(() => {
                  const { cleaned, citations } = extractCitations(desc);
                  return (
                    <div>
                      <Input
                        value={cleaned}
                        onChange={(e) => handleDescChange(code, e.target.value)}
                        className="h-8 text-sm"
                      />
                      {citations.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 text-xs">
                          {citations}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </TableCell>
              <TableCell className="p-1 text-center">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(code)}
                  aria-label="Remove code"
                >
                  ✕
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button variant="outline" size="sm" onClick={handleAddRow}>
        + Add Code
      </Button>
    </div>
  );
};

// ------------------ Treatment Outcome Editor ------------------

interface TreatmentOutcomeEditorProps {
  outcomes: Record<string, string>;
  onChange: (updated: Record<string, string>) => void;
}

const TREATMENT_OUTCOME_OPTIONS = [
  'UNKNOWN',
  'ONGOING_CARE',
  'DISCHARGED',
  'NOT_APPLICABLE_ONE_TIME_CARE',
] as const;

const TreatmentOutcomeEditor: React.FC<TreatmentOutcomeEditorProps> = ({
  outcomes,
  onChange,
}) => {
  const handleProviderNameChange = (oldName: string, newName: string) => {
    if (!newName || oldName === newName) return;
    const updated = { ...outcomes };
    const status = updated[oldName];
    delete updated[oldName];
    updated[newName] = status;
    onChange(updated);
  };

  const handleStatusChange = (providerName: string, newStatus: string) => {
    onChange({ ...outcomes, [providerName]: newStatus });
  };

  const handleRemove = (providerName: string) => {
    const updated = { ...outcomes };
    delete updated[providerName];
    onChange(updated);
  };

  const handleAddRow = () => {
    let idx = 1;
    let newKey = `New Provider ${idx}`;
    while (outcomes[newKey]) {
      idx += 1;
      newKey = `New Provider ${idx}`;
    }
    onChange({ ...outcomes, [newKey]: 'UNKNOWN' });
  };

  const entries = Object.entries(outcomes);

  return (
    <div>
      <Table className="border bg-muted/50 mb-2 text-sm">
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/2">Provider</TableHead>
            <TableHead className="w-1/2">Outcome</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-xs text-gray-500">
                —
              </TableCell>
            </TableRow>
          ) : (
            entries.map(([provider, status]) => (
              <TableRow key={provider} className="hover:bg-transparent">
                <TableCell className="p-1">
                  <Input
                    value={provider}
                    onChange={(e) =>
                      handleProviderNameChange(provider, e.target.value)
                    }
                    className="h-8 text-sm"
                    aria-label={`Provider name ${provider}`}
                  />
                </TableCell>
                <TableCell className="p-1">
                  <select
                    value={status}
                    onChange={(e) =>
                      handleStatusChange(provider, e.target.value)
                    }
                    className="h-8 w-full rounded-md border bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    aria-label={`Treatment outcome for ${provider}`}
                  >
                    {TREATMENT_OUTCOME_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                </TableCell>
                <TableCell className="p-1 text-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(provider)}
                    aria-label={`Remove ${provider}`}
                  >
                    ✕
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Button variant="outline" size="sm" onClick={handleAddRow}>
        + Add Provider
      </Button>
    </div>
  );
};

// ------------------ Inner Component ------------------

const DemandPanelInner = ({ panelMatterId }: { panelMatterId: string }) => {
  const matterId = panelMatterId;

  const { selectedCitation, setSelectedCitation } = useDemandCitation();

  const { data: matterContext, isLoading } = useGetMatterContextQuery(
    { matterId },
    { skip: !matterId },
  );

  const { data: artifacts } = useListArtifactsQuery(
    { matterId },
    { skip: !matterId },
  );

  const [updateMatterContext, { isLoading: isUpdating }] =
    useUpdateMatterContextMutation();

  const [generateContext, { isLoading: isGenerating }] =
    useGenerateContextMutation();

  const [draftDemand, { isLoading: isDrafting }] =
    useDraftDemandFromTemplateMutation();

  const [editableContext, setEditableContext] = useState<
    Record<string, unknown>
  >({});

  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isSetExhibitsModalOpen, setIsSetExhibitsModalOpen] = useState(false);

  // workspaceId available if needed later; currently unused

  const [expandedProviders, setExpandedProviders] = useState<
    Record<string, boolean>
  >({});

  // ICD-10 draft editing state (do not mutate editableContext until Save)
  const [icdDraft, setIcdDraft] = useState<Array<{
    key: string;
    code?: string;
    description: string;
    provider: string;
    tags: string[];
    chips: React.ReactNode[];
    rawTags: string[];
    index: number;
    isInferred?: boolean | null; // null = unknown, true = inferred, false = exact
  }> | null>(null);
  const [icdDraftDirty, setIcdDraftDirty] = useState(false);

  // Chronology row editing state
  const [editingVisitIndex, setEditingVisitIndex] = useState<number | null>(
    null,
  );
  const [visitDraft, setVisitDraft] = useState<{
    date: string;
    amount_billed: string;
    description: string;
    provider: string;
  }>({ date: '', amount_billed: '', description: '', provider: '' });
  const [visitDraftRawTags, setVisitDraftRawTags] = useState<string[]>([]);

  const handleToggleProvider = (providerKey: string) => {
    setExpandedProviders((prev) => ({
      ...prev,
      [providerKey]: !prev[providerKey],
    }));
  };

  const toDomId = (name: string) =>
    name
      ? `provider-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
      : 'provider-unknown';

  useEffect(() => {
    if (matterContext) {
      setEditableContext(matterContext);
    }
  }, [matterContext]);

  // Expand provider groups by default for chronology and ICD-10
  useEffect(() => {
    // Initialize ICD draft from current editableContext when it changes
    const value = (editableContext as Record<string, unknown>)?.icd_10_codes as
      | Array<{
          code?: string;
          description?: string;
          provider?: string | null;
          tags?: string[] | null;
          is_inferred?: boolean | null;
        }>
      | undefined;
    if (Array.isArray(value)) {
      const draft = value.map((e, idx) => {
        const desc = e?.description ?? '';
        const parsed =
          typeof desc === 'string'
            ? parseCitations(desc, matterId, setSelectedCitation)
            : { cleaned: String(desc ?? ''), chips: [], rawTags: [] };
        const rawDesc = String(desc ?? '');
        const providerMatch = rawDesc.match(/\[\s*provider\s*:\s*([^;\]]+)/i);
        const tagsMatch = rawDesc.match(/\[.*?tags\s*:\s*([^\]]+)\]/i);
        const inferredProvider = providerMatch
          ? providerMatch[1].trim()
          : undefined;
        const inferredTags = tagsMatch
          ? tagsMatch[1]
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
        const tags =
          Array.isArray(e.tags) && e.tags.length > 0
            ? (e.tags as string[])
            : inferredTags;
        const provider = (e.provider ?? inferredProvider ?? '') as string;
        const descriptionClean = parsed.cleaned.replace(
          /\s*\[[^\]]*\]\s*$/,
          '',
        );
        return {
          key: `${e.code ?? ''}-${idx}`,
          code: e.code,
          description: descriptionClean,
          provider,
          tags,
          chips: parsed.chips,
          rawTags: parsed.rawTags,
          index: idx,
          isInferred: e.is_inferred ?? null, // preserve null for unknown
        };
      });
      setIcdDraft(draft);
      setIcdDraftDirty(false);
    } else {
      setIcdDraft(null);
      setIcdDraftDirty(false);
    }

    const nextChrono: Record<string, boolean> = {};
    const mc = (editableContext as Record<string, unknown>)
      ?.medical_chronology as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(mc)) {
      mc.forEach((row) => {
        const prov = String(
          row.provider ?? row.treatment_provider ?? '',
        ).trim();
        const key = prov || 'Unknown Provider';
        nextChrono[key] = true;
      });
      if (Object.keys(nextChrono).length > 0) setExpandedProviders(nextChrono);
    }

    const nextIcd: Record<string, boolean> = {};
    const icd = (editableContext as Record<string, unknown>)?.icd_10_codes as
      | Array<{ description?: string; provider?: string | null }>
      | Record<string, string>
      | undefined;
    if (Array.isArray(icd)) {
      icd.forEach((e) => {
        let provider = (e?.provider || '').trim();
        if (!provider && typeof e?.description === 'string') {
          const m = e.description.match(/\[\s*provider\s*:\s*([^;\]]+)/i);
          if (m) provider = m[1].trim();
        }
        const key = provider || 'Unknown';
        nextIcd[key] = true;
      });
    } else if (icd && typeof icd === 'object') {
      // Legacy dict shape: parse provider from description if embedded
      Object.values(icd).forEach((desc) => {
        if (typeof desc === 'string') {
          const m = desc.match(/\[\s*provider\s*:\s*([^;\]]+)/i);
          const provider = (m ? m[1].trim() : '').trim();
          const key = provider || 'Unknown';
          nextIcd[key] = true;
        } else {
          nextIcd.Unknown = true;
        }
      });
    }
    if (Object.keys(nextIcd).length > 0) setExpandedIcdProviders(nextIcd);
    // Only when editableContext changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableContext]);

  const handleGenerateContext = async () => {
    if (!matterId) return;
    try {
      await generateContext({ matterId }).unwrap();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate context:', error);
    }
  };

  const handleSaveChanges = async () => {
    if (!matterId) return;
    const changedData: Record<string, unknown> = {};

    Object.keys(editableContext).forEach((key) => {
      if (
        (editableContext as Record<string, unknown>)[key] !==
        (matterContext as Record<string, unknown>)?.[key]
      ) {
        changedData[key] = (editableContext as Record<string, unknown>)[key];
      }
    });

    // If ICD draft has pending edits, include them in save payload and sync editableContext
    if (icdDraftDirty && Array.isArray(icdDraft)) {
      const dto = icdDraft.map((d) => ({
        code: d.code,
        description: `${d.description}${d.rawTags.join('')}`,
        provider: d.provider || undefined,
        tags: d.tags || [],
        is_inferred: d.isInferred, // preserve null for unknown
      }));
      changedData.icd_10_codes = dto;
      setEditableContext((prev) => ({ ...prev, icd_10_codes: dto }));
      setIcdDraftDirty(false);
    }

    if (Object.keys(changedData).length > 0) {
      await updateMatterContext({ matterId, data: changedData });
    }
  };

  const persistCaseContextPatch = async (patch: Record<string, unknown>) => {
    if (!matterId) return;
    try {
      await updateMatterContext({ matterId, data: patch });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist case context patch', e);
    }
  };

  // ------------ Medical Chronology helpers ------------
  const getChronologyArray = (): Array<Record<string, unknown>> => {
    const raw = (editableContext as Record<string, unknown>)
      ?.medical_chronology;
    return Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : [];
  };

  const getProviderNameFromRow = (row: Record<string, unknown>): string => {
    const providerNameRaw = String(
      (row.provider as string | undefined) ??
        (row.treatment_provider as string | undefined) ??
        '',
    ).trim();
    return providerNameRaw || 'Unknown Provider';
  };

  const getDateFromRow = (row: Record<string, unknown>): string => {
    return String(row.date ?? row.treatment_date ?? '');
  };

  const getAmountFromRow = (row: Record<string, unknown>): string => {
    return String(row.amount_billed ?? '');
  };

  const getDescriptionFromRow = (row: Record<string, unknown>): string => {
    return String(row.description ?? row.treatment ?? '');
  };

  const handleStartEditVisit = (rowIndex: number) => {
    const chronology = getChronologyArray();
    const row = chronology[rowIndex];
    if (!row) return;
    const provider = getProviderNameFromRow(row);
    const date = getDateFromRow(row);
    const amount = getAmountFromRow(row);
    const descRaw = getDescriptionFromRow(row);
    const { cleaned, rawTags } = parseCitations(
      descRaw,
      matterId,
      setSelectedCitation,
    );
    setVisitDraft({
      provider,
      date,
      amount_billed: amount,
      description: cleaned,
    });
    setVisitDraftRawTags(rawTags);
    setEditingVisitIndex(rowIndex);
    setExpandedProviders((prev) => ({ ...prev, [provider]: true }));
  };

  const handleCancelEditVisit = () => {
    setEditingVisitIndex(null);
    setVisitDraft({
      date: '',
      amount_billed: '',
      description: '',
      provider: '',
    });
    setVisitDraftRawTags([]);
  };

  const handleDraftFieldChange = (
    field: 'date' | 'amount_billed' | 'description' | 'provider',
    value: string,
  ) => {
    setVisitDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveVisitEdit = async () => {
    if (editingVisitIndex == null) return;
    const chronology = getChronologyArray();
    if (editingVisitIndex < 0 || editingVisitIndex >= chronology.length) return;
    const updatedRow: Record<string, string> = {
      provider: visitDraft.provider,
      date: visitDraft.date,
      amount_billed: visitDraft.amount_billed,
      treatment: visitDraft.description,
      citations: visitDraftRawTags.join(' '),
    };
    const newChronology = [...chronology];
    newChronology[editingVisitIndex] = updatedRow;
    setEditableContext((prev) => ({
      ...prev,
      medical_chronology: newChronology,
    }));
    // Persist immediately
    await persistCaseContextPatch({ medical_chronology: newChronology });
    handleCancelEditVisit();
  };

  const handleDeleteVisit = (rowIndex: number) => {
    const chronology = getChronologyArray();
    if (rowIndex < 0 || rowIndex >= chronology.length) return;
    const newChronology = chronology.filter((_, idx) => idx !== rowIndex);
    setEditableContext((prev) => ({
      ...prev,
      medical_chronology: newChronology,
    }));
    if (editingVisitIndex === rowIndex) {
      handleCancelEditVisit();
    }
  };

  const handleAddVisit = (providerName: string) => {
    const chronology = getChronologyArray();
    const newRow: Record<string, string> = {
      provider: providerName || 'Unknown Provider',
      date: '',
      amount_billed: '',
      treatment: '',
    };
    const newChronology = [...chronology, newRow];
    setEditableContext((prev) => ({
      ...prev,
      medical_chronology: newChronology,
    }));
    setExpandedProviders((prev) => ({
      ...prev,
      [providerName || 'Unknown Provider']: true,
    }));
    setVisitDraft({
      provider: providerName || 'Unknown Provider',
      date: '',
      amount_billed: '',
      description: '',
    });
    setVisitDraftRawTags([]);
    setEditingVisitIndex(newChronology.length - 1);
  };

  const formatFieldName = (key: string): string => {
    if (key.toLowerCase().endsWith('_id') || key.toLowerCase() === 'id') {
      return '';
    }
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getFieldIcon = (fieldName: string) => {
    const iconClass = 'h-4 w-4 text-gray-500';
    const fieldLower = fieldName.toLowerCase();

    if (fieldLower.includes('document_completeness'))
      return <ClipboardCheck className={iconClass} />;
    if (fieldLower.includes('coverage'))
      return <Shield className={iconClass} />;
    if (fieldLower.includes('liability'))
      return <AlertTriangle className={iconClass} />;
    if (fieldLower.includes('bill') || fieldLower.includes('max_coverage'))
      return <DollarSign className={iconClass} />;
    if (fieldLower.includes('icd') || fieldLower.includes('medical'))
      return <Heart className={iconClass} />;
    if (fieldLower.includes('chronology') || fieldLower.includes('date'))
      return <CalendarDays className={iconClass} />;
    if (fieldLower.includes('care') || fieldLower.includes('extent'))
      return <Activity className={iconClass} />;
    if (fieldLower.includes('unused')) return <FileX2 className={iconClass} />;
    if (fieldLower.includes('client') || fieldLower.includes('narrative'))
      return <User className={iconClass} />;

    return <FileText className={iconClass} />;
  };

  const handleIcdCodesChange = (updated: Record<string, string>) => {
    setEditableContext((prev) => ({ ...prev, icd_10_codes: updated }));
  };

  // Update a single ICD-10 entry in list DTO mode
  const handleIcdEntryChange = (
    index: number,
    patch: Partial<{ provider: string; tags: string[] }>,
  ) => {
    setIcdDraft((prev) => {
      if (!prev) return prev;
      const next = prev.map((e) =>
        e.index === index ? { ...e, ...patch } : e,
      );
      return next;
    });
    setIcdDraftDirty(true);
  };

  const handleTreatmentOutcomeChange = (updated: Record<string, string>) => {
    setEditableContext((prev) => ({ ...prev, treatment_outcome: updated }));
  };

  const handleTemplateConfirm = async (
    templateId: string,
    opts?: {
      workspaceId?: string;
      threadId?: string;
      runProofreading?: boolean;
    },
  ) => {
    if (!matterId) return;
    try {
      await draftDemand({
        matterId,
        templateId,
        workspaceId: opts?.workspaceId,
        threadId: opts?.threadId,
        runProofreading: opts?.runProofreading,
      }).unwrap();
      setIsTemplateDialogOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to draft demand:', e);
    }
  };

  // Expansion state for ICD-10 provider groups (separate from chronology)
  const [expandedIcdProviders, setExpandedIcdProviders] = useState<
    Record<string, boolean>
  >({});
  const [icdEditingIndex, setIcdEditingIndex] = useState<number | null>(null);
  const [icdRowDraft, setIcdRowDraft] = useState<{
    code: string;
    description: string;
    provider: string;
    tagsText: string;
    isInferred: boolean | null;
  }>({
    code: '',
    description: '',
    provider: '',
    tagsText: '',
    isInferred: null,
  });

  // State for adding new ICD-10 codes
  const [isAddingIcd, setIsAddingIcd] = useState(false);
  const [newIcdDraft, setNewIcdDraft] = useState<{
    code: string;
    description: string;
    provider: string;
    tagsText: string;
    isInferred: boolean | null;
  }>({
    code: '',
    description: '',
    provider: '',
    tagsText: '',
    isInferred: null,
  });
  const [providerPopoverOpen, setProviderPopoverOpen] = useState(false);

  // Compute available providers from case context
  const availableProviders = useMemo(() => {
    const providers = new Set<string>();

    // Get providers from medical chronology
    const mc = (editableContext as Record<string, unknown>)?.medical_chronology;
    if (Array.isArray(mc)) {
      mc.forEach((row: Record<string, unknown>) => {
        const prov = String(
          row?.provider ?? row?.treatment_provider ?? '',
        ).trim();
        if (prov) providers.add(prov);
      });
    }

    // Get providers from treatment outcomes
    const to = (editableContext as Record<string, unknown>)?.treatment_outcome;
    if (Array.isArray(to)) {
      (to as Array<{ provider?: string }>).forEach((entry) => {
        if (entry?.provider?.trim()) providers.add(entry.provider.trim());
      });
    } else if (to && typeof to === 'object') {
      Object.keys(to as Record<string, string>).forEach((prov) => {
        if (prov.trim()) providers.add(prov.trim());
      });
    }

    // Get providers from existing ICD-10 codes
    const icd = (editableContext as Record<string, unknown>)?.icd_10_codes;
    if (Array.isArray(icd)) {
      (icd as Icd10Entry[]).forEach((entry) => {
        if (entry?.provider?.trim()) providers.add(entry.provider.trim());
      });
    }

    return Array.from(providers).sort((a, b) => a.localeCompare(b));
  }, [editableContext]);

  const renderValue = (key: string, value: unknown): React.ReactNode => {
    if (key === 'icd_10_codes') {
      // New list DTO shape
      if (Array.isArray(value)) {
        const entries = value as Icd10Entry[];
        if (!entries.length) return <span className="text-sm">—</span>;

        // Preprocess entries to extract provider/tags and clean description
        const processed = (icdDraft ||
          entries.map((e, idx) => {
            const desc = e?.description;
            const parsed =
              typeof desc === 'string'
                ? parseCitations(desc, matterId, setSelectedCitation)
                : { cleaned: String(desc ?? ''), chips: [], rawTags: [] };
            const rawDesc = typeof desc === 'string' ? desc : '';
            const providerMatch = rawDesc.match(
              /\[\s*provider\s*:\s*([^;\]]+)/i,
            );
            const tagsMatch = rawDesc.match(/\[.*?tags\s*:\s*([^\]]+)\]/i);
            const inferredProvider = providerMatch
              ? providerMatch[1].trim()
              : undefined;
            const inferredTags = tagsMatch
              ? tagsMatch[1]
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [];
            const tags =
              Array.isArray(e.tags) && e.tags.length > 0
                ? e.tags
                : inferredTags;
            const provider = e.provider ?? inferredProvider ?? 'Unknown';
            const descriptionClean = parsed.cleaned.replace(
              /\s*\[[^\]]*\]\s*$/,
              '',
            );
            return {
              key: `${e.code}-${idx}`,
              code: e.code,
              index: idx,
              provider,
              tags,
              chips: parsed.chips,
              description: descriptionClean,
              isInferred: e.is_inferred ?? null, // preserve null for unknown
            };
          })) as typeof icdDraft;

        // Group by provider name
        const groups = new Map<string, NonNullable<typeof icdDraft>>();
        (processed || []).forEach((item) => {
          const list = groups.get(item.provider) || [];
          list.push(item);
          groups.set(item.provider, list);
        });
        const providerNames = Array.from(groups.keys()).sort((a, b) =>
          a.localeCompare(b),
        );

        return (
          <div>
            <Table className="border bg-muted/50 text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-48">Provider</TableHead>
                  <TableHead className="w-56">Tags</TableHead>
                  <TableHead className="w-24 text-center">Source</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providerNames.map((providerName, gIdx) => {
                  const expanded = !!expandedIcdProviders[providerName];
                  const items = groups.get(providerName)!;
                  return (
                    <React.Fragment key={`prov-${providerName}`}>
                      <TableRow className="bg-gray-50/60 hover:bg-gray-50">
                        <TableCell colSpan={6} className="p-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 text-left"
                            aria-expanded={expanded}
                            onClick={() =>
                              setExpandedIcdProviders((prev) => ({
                                ...prev,
                                [providerName]: !expanded,
                              }))
                            }
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <span className="font-semibold">
                              {providerName || 'Unknown'}
                            </span>
                            <span className="text-gray-500">
                              ({items.length})
                            </span>
                          </button>
                        </TableCell>
                      </TableRow>
                      {expanded &&
                        items.map((it) => {
                          const isEditing = icdEditingIndex === it.index;
                          return (
                            <TableRow key={it.key}>
                              <TableCell className="font-medium">
                                {isEditing ? (
                                  <Input
                                    value={icdRowDraft.code}
                                    onChange={(e) =>
                                      setIcdRowDraft((d) => ({
                                        ...d,
                                        code: e.target.value,
                                      }))
                                    }
                                    placeholder="ICD-10 Code"
                                    className="h-7 text-[11px] font-mono w-24"
                                    aria-label={`Edit code for ${it.code}`}
                                  />
                                ) : (
                                  it.code
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    value={icdRowDraft.description}
                                    onChange={(e) =>
                                      setIcdRowDraft((d) => ({
                                        ...d,
                                        description: e.target.value,
                                      }))
                                    }
                                    placeholder="Description"
                                    className="h-7 text-[11px]"
                                    aria-label={`Edit description for ${it.code}`}
                                  />
                                ) : (
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span>{it.description}</span>
                                    {it.chips?.length ? (
                                      <span className="flex gap-1">
                                        {it.chips}
                                      </span>
                                    ) : null}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    value={icdRowDraft.provider}
                                    onChange={(e) =>
                                      setIcdRowDraft((d) => ({
                                        ...d,
                                        provider: e.target.value,
                                      }))
                                    }
                                    placeholder="Provider"
                                    className="h-7 text-[11px]"
                                    aria-label={`Edit provider for code ${it.code}`}
                                  />
                                ) : (
                                  <span>{it.provider || 'Unknown'}</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    value={icdRowDraft.tagsText}
                                    onChange={(e) =>
                                      setIcdRowDraft((d) => ({
                                        ...d,
                                        tagsText: e.target.value,
                                      }))
                                    }
                                    placeholder="tag1, tag2"
                                    className="h-7 text-[11px]"
                                    aria-label={`Edit tags for code ${it.code}`}
                                  />
                                ) : it.tags && it.tags.length ? (
                                  <div className="flex flex-wrap gap-1">
                                    {it.tags.map((t, i2) => (
                                      <span
                                        key={`${it.key}-tag-${i2}`}
                                        className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700"
                                      >
                                        {t}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-gray-500">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {isEditing ? (
                                  <div className="flex justify-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setIcdRowDraft((d) => ({
                                          ...d,
                                          isInferred: false,
                                        }))
                                      }
                                      className={cn(
                                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border transition-colors',
                                        icdRowDraft.isInferred === false
                                          ? 'bg-green-100 text-green-700 border-green-300 ring-1 ring-green-400'
                                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-green-50 hover:text-green-600 hover:border-green-200',
                                      )}
                                      title="Code was found exactly in medical records"
                                    >
                                      Exact
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setIcdRowDraft((d) => ({
                                          ...d,
                                          isInferred: true,
                                        }))
                                      }
                                      className={cn(
                                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border transition-colors',
                                        icdRowDraft.isInferred === true
                                          ? 'bg-amber-100 text-amber-700 border-amber-300 ring-1 ring-amber-400'
                                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200',
                                      )}
                                      title="Code was inferred from injury description"
                                    >
                                      <Sparkles className="h-3 w-3" />
                                      Inferred
                                    </button>
                                  </div>
                                ) : it.isInferred === true ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 border border-amber-200"
                                    title="Extracted from injury description"
                                  >
                                    <Sparkles className="h-3 w-3" />
                                    Inferred
                                  </span>
                                ) : it.isInferred === false ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700 border border-green-200"
                                    title="Exact code found in medical records"
                                  >
                                    Exact
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200"
                                    title="Source not specified"
                                  >
                                    Unknown
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {isEditing ? (
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={async () => {
                                        const tagsArr = icdRowDraft.tagsText
                                          .split(',')
                                          .map((s) => s.trim())
                                          .filter(Boolean);
                                        // Update draft immediately
                                        const nextDraft = icdDraft
                                          ? [...icdDraft]
                                          : [];
                                        const idx = nextDraft.findIndex(
                                          (d) => d.index === it.index,
                                        );
                                        if (idx >= 0) {
                                          nextDraft[idx] = {
                                            ...nextDraft[idx],
                                            code: icdRowDraft.code,
                                            description:
                                              icdRowDraft.description,
                                            provider: icdRowDraft.provider,
                                            tags: tagsArr,
                                            isInferred: icdRowDraft.isInferred,
                                          } as (typeof nextDraft)[number];
                                        }
                                        setIcdDraft(nextDraft);
                                        setIcdDraftDirty(false);
                                        // Build DTO and persist full save now
                                        const dto = nextDraft.map((d) => ({
                                          code: d.code,
                                          description: `${d.description}${d.rawTags.join('')}`,
                                          provider: d.provider || undefined,
                                          tags: d.tags || [],
                                          is_inferred: d.isInferred, // preserve null for unknown
                                        }));
                                        await persistCaseContextPatch({
                                          icd_10_codes: dto,
                                        });
                                        setEditableContext((prev) => ({
                                          ...prev,
                                          icd_10_codes: dto,
                                        }));
                                        setIcdEditingIndex(null);
                                      }}
                                      aria-label="Save ICD"
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setIcdEditingIndex(null)}
                                      aria-label="Cancel ICD edit"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setIcdEditingIndex(it.index);
                                        setIcdRowDraft({
                                          code: it.code || '',
                                          description: it.description || '',
                                          provider:
                                            it.provider === 'Unknown'
                                              ? ''
                                              : it.provider,
                                          tagsText: (it.tags || []).join(', '),
                                          isInferred: it.isInferred ?? null,
                                        });
                                      }}
                                      aria-label={`Edit ICD ${it.code}`}
                                      title="Edit ICD"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={async () => {
                                        const nextDraft = icdDraft
                                          ? icdDraft.filter(
                                              (d) => d.index !== it.index,
                                            )
                                          : [];
                                        setIcdDraft(nextDraft);
                                        // Build DTO and persist
                                        const dto = nextDraft.map((d) => ({
                                          code: d.code,
                                          description: `${d.description}${d.rawTags.join('')}`,
                                          provider: d.provider || undefined,
                                          tags: d.tags || [],
                                          is_inferred: d.isInferred, // preserve null for unknown
                                        }));
                                        await persistCaseContextPatch({
                                          icd_10_codes: dto,
                                        });
                                        setEditableContext((prev) => ({
                                          ...prev,
                                          icd_10_codes: dto,
                                        }));
                                      }}
                                      aria-label={`Delete ICD ${it.code}`}
                                      title="Delete ICD"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
                {/* Add new ICD-10 code row */}
                {isAddingIcd && (
                  <TableRow className="bg-blue-50/50 dark:bg-cyan-950/50">
                    <TableCell className="p-1">
                      <Input
                        value={newIcdDraft.code}
                        onChange={(e) =>
                          setNewIcdDraft((d) => ({
                            ...d,
                            code: e.target.value,
                          }))
                        }
                        placeholder="e.g. S13.4"
                        className="h-7 text-[11px] font-mono"
                        aria-label="New ICD-10 code"
                        autoFocus
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        value={newIcdDraft.description}
                        onChange={(e) =>
                          setNewIcdDraft((d) => ({
                            ...d,
                            description: e.target.value,
                          }))
                        }
                        placeholder="Description"
                        className="h-7 text-[11px]"
                        aria-label="New ICD-10 description"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Popover
                        open={providerPopoverOpen}
                        onOpenChange={setProviderPopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={providerPopoverOpen}
                            className="h-7 w-full justify-between text-[11px] font-normal"
                          >
                            <span
                              className={cn(
                                'truncate',
                                !newIcdDraft.provider &&
                                  'text-muted-foreground',
                              )}
                            >
                              {newIcdDraft.provider || 'Select provider...'}
                            </span>
                            <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0" align="start">
                          <Command shouldFilter={false}>
                            <CommandInput
                              placeholder="Search or type new..."
                              value={newIcdDraft.provider}
                              onValueChange={(v) =>
                                setNewIcdDraft((d) => ({
                                  ...d,
                                  provider: v,
                                }))
                              }
                              className="h-8 text-[11px]"
                            />
                            <CommandList>
                              {/* Show "Create new" option when typing a name not in the list */}
                              {newIcdDraft.provider &&
                                !availableProviders.some(
                                  (p) =>
                                    p.toLowerCase() ===
                                    newIcdDraft.provider.toLowerCase(),
                                ) && (
                                  <CommandGroup>
                                    <CommandItem
                                      value={`create-${newIcdDraft.provider}`}
                                      onSelect={() => {
                                        setProviderPopoverOpen(false);
                                      }}
                                      className="text-[11px] text-blue-600"
                                    >
                                      <Plus className="mr-2 h-3 w-3" />
                                      Create &quot;{newIcdDraft.provider}&quot;
                                    </CommandItem>
                                  </CommandGroup>
                                )}
                              {availableProviders.length > 0 && (
                                <CommandGroup heading="Existing Providers">
                                  {availableProviders
                                    .filter(
                                      (p) =>
                                        !newIcdDraft.provider ||
                                        p
                                          .toLowerCase()
                                          .includes(
                                            newIcdDraft.provider.toLowerCase(),
                                          ),
                                    )
                                    .map((provider) => (
                                      <CommandItem
                                        key={provider}
                                        value={provider}
                                        onSelect={() => {
                                          setNewIcdDraft((d) => ({
                                            ...d,
                                            provider,
                                          }));
                                          setProviderPopoverOpen(false);
                                        }}
                                        className="text-[11px]"
                                      >
                                        <Check
                                          className={cn(
                                            'mr-2 h-3 w-3',
                                            newIcdDraft.provider === provider
                                              ? 'opacity-100'
                                              : 'opacity-0',
                                          )}
                                        />
                                        {provider}
                                      </CommandItem>
                                    ))}
                                </CommandGroup>
                              )}
                              {availableProviders.length === 0 &&
                                !newIcdDraft.provider && (
                                  <CommandEmpty className="py-2 px-3 text-[11px] text-muted-foreground">
                                    Type to add a new provider
                                  </CommandEmpty>
                                )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        value={newIcdDraft.tagsText}
                        onChange={(e) =>
                          setNewIcdDraft((d) => ({
                            ...d,
                            tagsText: e.target.value,
                          }))
                        }
                        placeholder="tag1, tag2"
                        className="h-7 text-[11px]"
                        aria-label="New ICD-10 tags"
                      />
                    </TableCell>
                    <TableCell className="p-1 text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            setNewIcdDraft((d) => ({
                              ...d,
                              isInferred: false,
                            }))
                          }
                          className={cn(
                            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border transition-colors',
                            newIcdDraft.isInferred === false ||
                              newIcdDraft.isInferred === null
                              ? 'bg-green-100 text-green-700 border-green-300 ring-1 ring-green-400'
                              : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-green-50 hover:text-green-600 hover:border-green-200',
                          )}
                          title="Code was found exactly in medical records"
                        >
                          Exact
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setNewIcdDraft((d) => ({
                              ...d,
                              isInferred: true,
                            }))
                          }
                          className={cn(
                            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border transition-colors',
                            newIcdDraft.isInferred === true
                              ? 'bg-amber-100 text-amber-700 border-amber-300 ring-1 ring-amber-400'
                              : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200',
                          )}
                          title="Code was inferred from injury description"
                        >
                          <Sparkles className="h-3 w-3" />
                          Inferred
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="p-1 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!newIcdDraft.code.trim()}
                          onClick={async () => {
                            const tagsArr = newIcdDraft.tagsText
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean);
                            const newEntry = {
                              code: newIcdDraft.code.trim(),
                              description: newIcdDraft.description.trim(),
                              provider:
                                newIcdDraft.provider.trim() || undefined,
                              tags: tagsArr.length > 0 ? tagsArr : [],
                            };
                            // Add to draft
                            const nextDraft = icdDraft ? [...icdDraft] : [];
                            const newIndex =
                              nextDraft.length > 0
                                ? Math.max(...nextDraft.map((d) => d.index)) + 1
                                : 0;
                            nextDraft.push({
                              key: `${newEntry.code}-${newIndex}`,
                              code: newEntry.code,
                              description: newEntry.description,
                              provider: newEntry.provider || 'Unknown',
                              tags: newEntry.tags,
                              chips: [],
                              rawTags: [],
                              index: newIndex,
                              isInferred:
                                newIcdDraft.isInferred === null
                                  ? false
                                  : newIcdDraft.isInferred,
                            });
                            setIcdDraft(nextDraft);
                            // Build DTO and persist
                            const dto = nextDraft.map((d) => ({
                              code: d.code,
                              description: `${d.description}${d.rawTags.join('')}`,
                              provider:
                                d.provider && d.provider !== 'Unknown'
                                  ? d.provider
                                  : undefined,
                              tags: d.tags || [],
                              is_inferred: d.isInferred, // preserve null for unknown
                            }));
                            await persistCaseContextPatch({
                              icd_10_codes: dto,
                            });
                            setEditableContext((prev) => ({
                              ...prev,
                              icd_10_codes: dto,
                            }));
                            // Reset add state
                            setNewIcdDraft({
                              code: '',
                              description: '',
                              provider: '',
                              tagsText: '',
                              isInferred: null,
                            });
                            setIsAddingIcd(false);
                            setProviderPopoverOpen(false);
                            // Expand the provider group so user can see the new entry
                            const providerKey = newEntry.provider || 'Unknown';
                            setExpandedIcdProviders((prev) => ({
                              ...prev,
                              [providerKey]: true,
                            }));
                          }}
                          aria-label="Save new ICD-10 code"
                        >
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setIsAddingIcd(false);
                            setProviderPopoverOpen(false);
                            setNewIcdDraft({
                              code: '',
                              description: '',
                              provider: '',
                              tagsText: '',
                              isInferred: null,
                            });
                          }}
                          aria-label="Cancel adding ICD-10 code"
                        >
                          Cancel
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {/* Add ICD-10 Code button */}
            {!isAddingIcd && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setIsAddingIcd(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add ICD-10 Code
              </Button>
            )}
          </div>
        );
      }
      // Legacy dict shape
      if (value && typeof value === 'object') {
        return (
          <Icd10CodesEditor
            codes={value as Record<string, string>}
            onChange={handleIcdCodesChange}
            matterId={matterId}
          />
        );
      }
      return <span className="text-sm">—</span>;
    }

    if (key === 'treatment_outcome') {
      // Handle new list DTO shape by mapping to provider->status dict for the editor
      if (Array.isArray(value)) {
        const dict: Record<string, string> = {};
        (value as Array<{ provider?: string; status?: string }>).forEach(
          (e) => {
            if (e?.provider) dict[e.provider] = e?.status ?? '';
          },
        );
        return (
          <TreatmentOutcomeEditor
            outcomes={dict}
            onChange={handleTreatmentOutcomeChange}
          />
        );
      }
      if (value && typeof value === 'object') {
        return (
          <TreatmentOutcomeEditor
            outcomes={value as Record<string, string>}
            onChange={handleTreatmentOutcomeChange}
          />
        );
      }
      return <span className="text-sm">—</span>;
    }

    // Document Completeness Assessment - render with dedicated card component
    if (key === 'document_completeness_assessment') {
      return (
        <DocumentCompletenessCard
          assessment={value as DocumentCompletenessAssessment | null}
          artifacts={artifacts}
        />
      );
    }

    // Demand Readiness - render with dedicated card component
    if (key === 'demand_readiness') {
      return (
        <DemandReadinessCard
          demandReadiness={value as DemandReadiness | null}
        />
      );
    }

    // Analyzed Artifact IDs - render as clickable file pills with names
    if (key === 'analyzed_artifact_ids' && Array.isArray(value)) {
      const artifactIds = value as string[];
      if (artifactIds.length === 0) {
        return (
          <span className="text-sm text-gray-400 italic">
            No files analyzed yet
          </span>
        );
      }

      // Map artifact IDs to their names using the artifacts data
      const artifactMap = new Map(artifacts?.map((a) => [a.id, a]) || []);

      return (
        <div className="flex flex-wrap gap-2">
          {artifactIds.map((artifactId) => {
            const artifact = artifactMap.get(artifactId);
            const displayName = artifact
              ? artifact.blob?.name ||
                artifact.artifact_digest?.title ||
                artifact.artifact_digest?.file_name ||
                `Document ${artifactId.slice(-8)}`
              : `Unknown (${artifactId.slice(-8)})`;
            const downloadUrl = artifact?.blob?.download_url;

            return (
              <a
                key={artifactId}
                href={downloadUrl || '#'}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 dark:bg-cyan-200 dark:text-cyan-900 dark:border-cyan-300 dark:hover:bg-cyan-100 transition-all shadow-sm"
                title={displayName}
              >
                <FileText className="h-3 w-3" />
                <span className="truncate max-w-[180px]">{displayName}</span>
              </a>
            );
          })}
        </div>
      );
    }

    // Unused Docs - render as a clean table with document names and reasons
    if (key === 'unused_docs' && value && typeof value === 'object') {
      const unusedDocs = value as Record<string, string>;
      const entries = Object.entries(unusedDocs);

      if (entries.length === 0) {
        return (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <Check className="h-4 w-4" />
            <span>All documents are being used</span>
          </div>
        );
      }

      // Try to map document IDs to artifact names
      const artifactMap = new Map(artifacts?.map((a) => [a.id, a]) || []);

      return (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="bg-gray-50/80">
                <TableHead className="font-medium text-gray-600 py-3 px-4">
                  Document
                </TableHead>
                <TableHead className="font-medium text-gray-600 py-3 px-4">
                  Reason Unused
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(([docKey, reason], idx) => {
                // Try to get the artifact info for this document
                const artifact = artifactMap.get(docKey);
                const displayName = artifact
                  ? artifact.blob?.name ||
                    artifact.artifact_digest?.title ||
                    artifact.artifact_digest?.file_name ||
                    docKey
                  : docKey;
                const downloadUrl = artifact?.blob?.download_url;

                return (
                  <TableRow
                    key={docKey}
                    className={cn(
                      'hover:bg-gray-50/50 transition-colors',
                      idx % 2 === 1 && 'bg-gray-50/30',
                    )}
                  >
                    <TableCell className="py-3 px-4">
                      {downloadUrl ? (
                        <a
                          href={downloadUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:underline"
                          title={`View: ${displayName}`}
                        >
                          <FileX2 className="h-4 w-4 text-gray-400" />
                          <span className="font-medium">{displayName}</span>
                        </a>
                      ) : (
                        <div className="flex items-center gap-2">
                          <FileX2 className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-700">
                            {displayName}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-gray-600">
                      {reason}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="px-4 py-2.5 bg-amber-50/50 border-t border-amber-100 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-xs text-amber-700">
              {entries.length} document{entries.length !== 1 ? 's' : ''} not
              used in case context
            </span>
          </div>
        </div>
      );
    }

    // Render medical chronology grouped by provider with expandable per-visit details
    if (key === 'medical_chronology') {
      // Handle null/undefined/empty cases
      if (!Array.isArray(value) || value.length === 0) {
        return <span className="text-sm">—</span>;
      }

      const chronology = value as Array<{
        date?: string;
        treatment_date?: string;
        provider?: string;
        treatment_provider?: string;
        amount_billed?: string;
        description?: string;
        treatment?: string;
      }>;

      // Helpers
      const parseAmount = (v: unknown): number => {
        if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
        const str = String(v ?? '').replace(/[^0-9.-]/g, '');
        const num = parseFloat(str);
        return Number.isFinite(num) ? num : 0;
      };

      const formatMoney = (n: number): string =>
        n.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        });

      const getDateValue = (row: Record<string, unknown>): number => {
        const raw = String(row.date ?? row.treatment_date ?? '');
        const d = new Date(raw);
        const t = d.getTime();
        return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
      };

      // Build a quick lookup to original chronology indices
      const chronologyRows: Array<Record<string, unknown>> = (
        editableContext as Record<string, unknown>
      ).medical_chronology as Array<Record<string, unknown>>;
      const rowIndexMap = new Map<object, number>();
      chronologyRows?.forEach((row, idx) => {
        rowIndexMap.set(row as object, idx);
      });

      // Group by provider
      const groupsMap = new Map<string, Array<Record<string, unknown>>>();
      chronology.forEach((row) => {
        const r = row as Record<string, unknown>;
        const providerName = getProviderNameFromRow(r);
        const existing = groupsMap.get(providerName) || [];
        existing.push(r);
        groupsMap.set(providerName, existing);
      });

      const providerGroups = Array.from(groupsMap.entries())
        .map(([providerName, visits]) => {
          const total = visits.reduce(
            (sum, v) =>
              sum + parseAmount((v as Record<string, unknown>).amount_billed),
            0,
          );
          visits.sort((a, b) => getDateValue(a) - getDateValue(b));
          return { providerName, visits, total };
        })
        .sort((a, b) => a.providerName.localeCompare(b.providerName));

      // Use backend total_medical_bills if available, otherwise sum provider totals
      const backendTotal = parseAmount(
        (editableContext as Record<string, unknown>)?.total_medical_bills,
      );
      const grandTotal =
        backendTotal > 0
          ? backendTotal
          : providerGroups.reduce((sum, group) => sum + group.total, 0);

      return (
        <Table className="border bg-muted/50 text-xs">
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead className="w-32 text-right">Total Billed</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {providerGroups.map(({ providerName, visits, total }, gIdx) => {
              const expanded = !!expandedProviders[providerName];
              const controlsId = `${toDomId(providerName)}-${gIdx}`;
              return (
                <React.Fragment key={providerName}>
                  <TableRow className="bg-gray-50/60 hover:bg-gray-50">
                    <TableCell className="p-2">
                      <button
                        type="button"
                        className="flex items-center gap-2 text-left w-full focus:outline-none focus:ring-2 focus:ring-primary/50 rounded px-1"
                        onClick={() => handleToggleProvider(providerName)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggleProvider(providerName);
                          }
                        }}
                        aria-expanded={expanded}
                        aria-controls={controlsId}
                        aria-label={`Toggle ${providerName} visits`}
                        tabIndex={0}
                      >
                        {expanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="font-medium text-gray-900">
                          {providerName || 'Unknown'}
                        </span>
                      </button>
                    </TableCell>
                    <TableCell className="p-2 text-right font-medium">
                      {formatMoney(total)}
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleAddVisit(providerName)}
                        aria-label={`Add visit for ${providerName}`}
                        title="Add Visit"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleProvider(providerName)}
                        aria-label={
                          expanded
                            ? `Collapse ${providerName}`
                            : `Expand ${providerName}`
                        }
                      >
                        {expanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={3} className="p-0">
                        <Table className="text-[11px]">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-40">Provider</TableHead>
                              <TableHead className="w-28">Date</TableHead>
                              <TableHead className="w-24 text-right">
                                Amount
                              </TableHead>
                              <TableHead>Treatment Details</TableHead>
                              <TableHead className="w-32 text-right">
                                Actions
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody id={controlsId}>
                            {visits.map((row, idx) => {
                              const r = row as Record<string, unknown>;
                              const origIdx =
                                rowIndexMap.get(r as object) ?? -1;
                              const isEditingVisit =
                                editingVisitIndex === origIdx;
                              const desc = getDescriptionFromRow(r);
                              const { cleaned, chips } = parseCitations(
                                desc,
                                matterId,
                                setSelectedCitation,
                              );
                              const dateStr = getDateFromRow(r);
                              const amountStr = getAmountFromRow(r);
                              return (
                                <TableRow
                                  key={`visit-${providerName}-${idx}`}
                                  className="align-top hover:bg-transparent"
                                >
                                  {isEditingVisit ? (
                                    <>
                                      <TableCell className="p-1">
                                        <Input
                                          value={visitDraft.provider}
                                          onChange={(e) =>
                                            handleDraftFieldChange(
                                              'provider',
                                              e.target.value,
                                            )
                                          }
                                          className="h-7 text-[11px]"
                                          aria-label={`Edit provider for visit ${idx + 1} at ${providerName}`}
                                        />
                                      </TableCell>
                                      <TableCell className="p-1">
                                        <Input
                                          value={visitDraft.date}
                                          onChange={(e) =>
                                            handleDraftFieldChange(
                                              'date',
                                              e.target.value,
                                            )
                                          }
                                          className="h-7 text-[11px]"
                                          aria-label={`Edit date for visit ${idx + 1} at ${providerName}`}
                                        />
                                      </TableCell>
                                      <TableCell className="p-1 text-right">
                                        <Input
                                          value={visitDraft.amount_billed}
                                          onChange={(e) =>
                                            handleDraftFieldChange(
                                              'amount_billed',
                                              e.target.value,
                                            )
                                          }
                                          inputMode="decimal"
                                          className="h-7 text-[11px] text-right"
                                          aria-label={`Edit amount for visit ${idx + 1} at ${providerName}`}
                                        />
                                      </TableCell>
                                      <TableCell className="p-1 whitespace-pre-wrap">
                                        <textarea
                                          className="w-full min-h-[60px] resize-y rounded-md border bg-background p-2 text-[11px] shadow-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                                          rows={3}
                                          value={visitDraft.description}
                                          onChange={(e) =>
                                            handleDraftFieldChange(
                                              'description',
                                              e.target.value,
                                            )
                                          }
                                          aria-label={`Edit description for visit ${idx + 1} at ${providerName}`}
                                        />
                                      </TableCell>
                                      <TableCell className="p-1 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleSaveVisitEdit}
                                            aria-label="Save visit"
                                          >
                                            Save
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleCancelEditVisit}
                                            aria-label="Cancel edit"
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </>
                                  ) : (
                                    <>
                                      <TableCell className="p-1">
                                        {providerName}
                                      </TableCell>
                                      <TableCell className="p-1">
                                        {dateStr}
                                      </TableCell>
                                      <TableCell className="p-1 text-right">
                                        {amountStr}
                                      </TableCell>
                                      <TableCell className="p-1 whitespace-pre-wrap">
                                        {cleaned ||
                                          String(
                                            r.treatment ?? r.description ?? '',
                                          )}
                                        {chips.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mt-1 text-[10px]">
                                            {chips}
                                          </div>
                                        )}
                                      </TableCell>
                                      <TableCell className="p-1 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() =>
                                              handleStartEditVisit(origIdx)
                                            }
                                            aria-label={`Edit visit ${idx + 1} for ${providerName}`}
                                            title="Edit Visit"
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() =>
                                              handleDeleteVisit(origIdx)
                                            }
                                            aria-label={`Delete visit ${idx + 1} for ${providerName}`}
                                            title="Delete Visit"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {/* Grand Total Row */}
            <TableRow className="bg-blue-50 dark:bg-cyan-950 border-t-2 border-blue-200 dark:border-cyan-700 font-semibold">
              <TableCell className="p-3">
                <span className="text-blue-900 dark:text-cyan-200 font-bold">
                  Grand Total
                </span>
              </TableCell>
              <TableCell className="p-3 text-right text-blue-900 dark:text-cyan-200 font-bold">
                {formatMoney(grandTotal)}
              </TableCell>
              <TableCell className="p-3" />
            </TableRow>
          </TableBody>
        </Table>
      );
    }

    const isDate =
      key.toLowerCase().includes('created_at') ||
      key.toLowerCase().includes('updated_at');

    if (isDate) {
      try {
        const date = new Date(value as string | number | Date);
        return format(date, 'MMM d, yyyy h:mm a');
      } catch {
        return String(value);
      }
    }

    if (typeof value === 'object' && value !== null) {
      return (
        <pre className="text-xs bg-gray-100 p-2 rounded whitespace-pre-wrap">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    const stringVal = String(value || '');

    const { cleaned, chips, rawTags } = parseCitations(
      stringVal,
      matterId,
      setSelectedCitation,
    );

    const shouldUseTextarea =
      cleaned.length > 120 ||
      [
        'coverage_details',
        'liability_description',
        'extent_of_care',
        'client_narrative',
      ].includes(key);

    const handleFieldUpdate = (newCleaned: string) => {
      const recomposed = `${newCleaned}${rawTags.join('')}`;
      setEditableContext((prev) => ({ ...prev, [key]: recomposed }));
    };

    return (
      <div className="w-full">
        {shouldUseTextarea ? (
          <textarea
            className="w-full min-h-[120px] resize-y rounded-md border bg-background p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            rows={6}
            value={cleaned}
            onChange={(e) => handleFieldUpdate(e.target.value)}
          />
        ) : (
          <Input
            value={cleaned}
            onChange={(e) => handleFieldUpdate(e.target.value)}
            className="h-10 px-3 text-sm"
          />
        )}
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1 text-xs">{chips}</div>
        )}
      </div>
    );
  };

  // Build items but skip *_explanation standalone rows. We'll attach them to their main field.
  // Also skip total_medical_bills since it's shown in the Medical Chronology table.
  const items = Object.entries(editableContext)
    .filter(([k]) => !k.endsWith('_explanation') && k !== 'total_medical_bills')
    .map(([key, value]) => ({
      key: formatFieldName(key),
      originalKey: key,
      value,
      explanation: (editableContext as Record<string, unknown>)[
        `${key}_explanation`
      ],
    }))
    .filter((item) => item.key !== '' && item.originalKey !== 'matter');

  if (isLoading)
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  if (!matterContext)
    return (
      <div className="text-center text-sm text-gray-500">
        No context available.
      </div>
    );

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 to-gray-100/80">
      {/* Header - Clean and minimal */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200/80">
        <div className="px-8 py-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg shadow-blue-500/20">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 tracking-tight">
                  Case Context
                </h1>
                <p className="text-sm text-gray-500">
                  Review and approve case information
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CaseContextBulkApproval
                matterId={matterId}
                matterContext={matterContext}
              />
              <div className="h-6 w-px bg-gray-200 mx-1" />
              <Button
                onClick={() => setIsSetExhibitsModalOpen(true)}
                variant="outline"
                size="sm"
                className="gap-2 rounded-lg"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Set Exhibits
              </Button>
              <CaseContextGenerationSpinner matterId={matterId} mode="inline">
                <Button
                  onClick={handleGenerateContext}
                  disabled={isGenerating}
                  variant="outline"
                  size="sm"
                  className="gap-2 rounded-lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </>
                  )}
                </Button>
              </CaseContextGenerationSpinner>
              <DemandGenerationSpinner
                matterId={matterId}
                threadId={undefined}
                mode="inline"
                className=""
              >
                <Button
                  onClick={() => setIsTemplateDialogOpen(true)}
                  disabled={isDrafting}
                  size="sm"
                  className="gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25"
                >
                  {isDrafting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Drafting…
                    </>
                  ) : (
                    <>
                      <FileText className="h-3.5 w-3.5" />
                      Draft Demand
                    </>
                  )}
                </Button>
              </DemandGenerationSpinner>
              <Button
                onClick={handleSaveChanges}
                disabled={isUpdating}
                variant="secondary"
                size="sm"
                className="gap-2 rounded-lg"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Elegant scrolling area */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* Demand Readiness Card - shown at top when data exists */}
        {matterContext?.demand_readiness && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-5 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-blue-100">
                <ClipboardCheck className="h-4 w-4 text-blue-600" />
              </div>
              <h2 className="text-base font-medium text-gray-900">
                Demand Readiness
              </h2>
            </div>
            <DemandReadinessCard
              demandReadiness={
                matterContext.demand_readiness as DemandReadiness
              }
            />
          </div>
        )}

        {/* File Analysis Status Card */}
        {artifacts && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'p-2 rounded-lg',
                    artifacts
                      .filter((a) => a.artifact_type === 'BLOB')
                      .filter((a) => {
                        const analyzed = new Set(
                          matterContext?.analyzed_artifact_ids || [],
                        );
                        return !analyzed.has(a.id);
                      }).length === 0
                      ? 'bg-green-100'
                      : 'bg-amber-100',
                  )}
                >
                  {artifacts
                    .filter((a) => a.artifact_type === 'BLOB')
                    .filter((a) => {
                      const analyzed = new Set(
                        matterContext?.analyzed_artifact_ids || [],
                      );
                      return !analyzed.has(a.id);
                    }).length === 0 ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                </div>
                <h2 className="text-base font-medium text-gray-900">
                  {artifacts
                    .filter((a) => a.artifact_type === 'BLOB')
                    .filter((a) => {
                      const analyzed = new Set(
                        matterContext?.analyzed_artifact_ids || [],
                      );
                      return !analyzed.has(a.id);
                    }).length === 0
                    ? 'All Files Analyzed'
                    : 'Files Pending Analysis'}
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Pills for analyzed files */}
              <div className="bg-gray-50/80 rounded-xl p-4 md:col-span-2">
                <div className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">
                  Analyzed Files
                </div>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {(() => {
                    const analyzedIds = new Set(
                      matterContext?.analyzed_artifact_ids || [],
                    );
                    const analyzed = artifacts
                      .filter((a) => a.artifact_type === 'BLOB')
                      .filter((a) => analyzedIds.has(a.id));

                    if (analyzed.length === 0) {
                      return <div className="text-xs text-gray-500">—</div>;
                    }

                    return analyzed.map((a) => {
                      const displayName =
                        a.blob?.name ||
                        a.artifact_digest?.title ||
                        a.artifact_digest?.file_name ||
                        `Document ${a.id.slice(-8)}`;
                      return (
                        <a
                          key={`analyzed-${a.id}`}
                          href={a.blob?.download_url || '#'}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-all shadow-sm"
                          title={displayName}
                        >
                          <Check className="h-3 w-3 text-green-500" />
                          <span className="truncate max-w-[140px]">
                            {displayName}
                          </span>
                        </a>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Pills for not analyzed files */}
              <div className="bg-amber-50/50 rounded-xl p-4 md:col-span-1">
                <div className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">
                  Pending Analysis
                </div>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {(() => {
                    const analyzed = new Set(
                      matterContext?.analyzed_artifact_ids || [],
                    );
                    const notAnalyzed = artifacts
                      .filter((a) => a.artifact_type === 'BLOB')
                      .filter((a) => !analyzed.has(a.id));

                    if (notAnalyzed.length === 0) {
                      return (
                        <div className="text-xs text-gray-500 italic">
                          All files analyzed
                        </div>
                      );
                    }

                    return notAnalyzed.map((a) => {
                      const displayName =
                        a.blob?.name ||
                        a.artifact_digest?.title ||
                        a.artifact_digest?.file_name ||
                        `Document ${a.id.slice(-8)}`;
                      return (
                        <a
                          key={`not-analyzed-${a.id}`}
                          href={a.blob?.download_url || '#'}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-white text-amber-700 border border-amber-200 hover:border-amber-400 hover:bg-amber-100 transition-all shadow-sm"
                          title={displayName}
                        >
                          <span className="truncate max-w-[120px]">
                            {displayName}
                          </span>
                        </a>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
        <TooltipProvider>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-gray-50 to-slate-50 hover:bg-gray-50">
                  <TableHead className="w-[220px] font-medium text-gray-600 py-4 px-6 text-xs uppercase tracking-wide">
                    Field
                  </TableHead>
                  <TableHead className="font-medium text-gray-600 py-4 text-xs uppercase tracking-wide">
                    Content
                  </TableHead>
                  <TableHead className="w-[120px] font-medium text-gray-600 py-4 text-xs uppercase tracking-wide text-center">
                    Status
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="h-48 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <div className="p-4 bg-gray-100 rounded-2xl mb-4">
                          <FileText className="h-10 w-10 text-gray-300" />
                        </div>
                        <p className="text-base font-medium text-gray-600">
                          No case context yet
                        </p>
                        <p className="text-sm mt-1 text-gray-400 max-w-xs">
                          Click &ldquo;Generate&rdquo; to analyze your documents
                          and build the case context
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    // Get the approval info for this field
                    const approvalKey = `${item.originalKey}_approval`;
                    const approval = matterContext?.[approvalKey];
                    const isApproved = approval?.approved ?? false;

                    // Skip certain fields from display
                    if (
                      item.originalKey.endsWith('_explanation') ||
                      item.originalKey.endsWith('_approval') ||
                      ['id', 'matter_id', 'created_at', 'updated_at'].includes(
                        item.originalKey,
                      )
                    ) {
                      return null;
                    }

                    return (
                      <TableRow
                        key={item.originalKey}
                        className={cn(
                          'group border-b border-gray-100 last:border-0',
                          'hover:bg-gradient-to-r hover:from-blue-50/40 hover:to-transparent transition-all duration-200',
                          isApproved && 'bg-green-50/20',
                        )}
                      >
                        <TableCell className="font-medium py-5 px-6 align-top">
                          <div className="flex items-start gap-3">
                            <div
                              className={cn(
                                'p-2 rounded-lg mt-0.5',
                                isApproved ? 'bg-green-100' : 'bg-gray-100',
                              )}
                            >
                              {getFieldIcon(item.originalKey)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-900 text-sm font-medium">
                                  {item.key}
                                </span>
                                {isApproved && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700">
                                    <Check className="h-2.5 w-2.5" />
                                    Approved
                                  </span>
                                )}
                              </div>
                              {item.explanation != null && (
                                <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                  <Sparkles className="h-3 w-3" />
                                  AI analysis available
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-5 align-top">
                          <div className="space-y-3">
                            <div className="text-gray-700">
                              {
                                renderValue(
                                  item.originalKey,
                                  item.value,
                                ) as React.ReactNode
                              }
                            </div>
                            {item.explanation != null ? (
                              <details className="group/details">
                                <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5 select-none">
                                  <ChevronRight className="h-3.5 w-3.5 transition-transform group-open/details:rotate-90" />
                                  View AI Explanation
                                </summary>
                                <div className="mt-3 p-5 bg-gradient-to-br from-blue-50/80 to-indigo-50/50 rounded-xl border border-blue-100/80 min-w-[320px]">
                                  <div className="flex items-start gap-3">
                                    <Sparkles className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-gray-600 leading-relaxed flex-1 min-w-0">
                                      {
                                        renderValue(
                                          `${item.originalKey}_explanation`,
                                          item.explanation,
                                        ) as React.ReactNode
                                      }
                                    </div>
                                  </div>
                                </div>
                              </details>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="py-5 align-top">
                          <div className="flex items-center justify-center pt-1">
                            <CaseContextApprovalToggle
                              matterId={matterId}
                              fieldName={item.originalKey}
                              approval={approval}
                              compact
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TooltipProvider>
      </div>

      {/* Citation Viewer */}
      {selectedCitation && (
        <Dialog open onOpenChange={() => setSelectedCitation(null)}>
          <DialogContent className="w-full max-w-5xl h-[80vh]">
            <CitationViewer
              url={selectedCitation.materializedCitation.url}
              citations={selectedCitation.materializedCitation.blocks}
              selectedCitationId={selectedCitation.selectedBlockId}
              setSelectedCitationId={(id: string) =>
                setSelectedCitation({
                  ...selectedCitation,
                  selectedBlockId: id,
                })
              }
            />
          </DialogContent>
        </Dialog>
      )}

      <TemplatePickerDialog
        open={isTemplateDialogOpen}
        onOpenChange={setIsTemplateDialogOpen}
        onConfirm={handleTemplateConfirm}
        matterId={matterId}
      />
      <SetExhibitsModal
        matterId={matterId}
        open={isSetExhibitsModalOpen}
        onOpenChange={setIsSetExhibitsModalOpen}
      />
    </div>
  );
};

// Wrapper
export const DemandPanel: React.FC = () => {
  const { selectedMatterData } = useMatterSelection();
  const matterId = selectedMatterData?.id || '';

  return (
    <DemandCitationProvider>
      <DemandPanelInner panelMatterId={matterId} />
    </DemandCitationProvider>
  );
};
